import { FC } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from 'hooks/useTranslate';

import IconTooltip from 'modules/_shared/components/Icon/IconTooltip';

import { useEnvelopesListSummarySection } from '../hooks/useEnvelopesListSummarySection';

const EnvelopesListSummarySection: FC = () => {
  const { t } = useTranslate();
  const { societyId } = useParams<{ societyId?: string }>();

  const { data: metrics, isLoading } =
    useEnvelopesListSummarySection(societyId);

  const {
    sentEnvelopes,
    pendingEnvelopes,
    completedEnvelopes,
    exceededMaxEnvelopes,
  } = metrics;

  const summaryCards = [
    {
      key: 'sent-card',
      value: sentEnvelopes,
      header: t('EnvelopesSent'),
      title: t('documents'),
      description: t('EnvelopesSentDescription'),
      color: 'primary-raw',
      icon: 'ni-send',
      tooltip: '',
    },
    {
      key: 'pending-card',
      value: pendingEnvelopes,
      header: t('EnvelopesPending'),
      title: t('documents'),
      description: t('EnvelopesPendingDescription'),
      color: 'info',
      icon: 'ni-clock',
      tooltip: exceededMaxEnvelopes
        ? t('EnvelopesCardTooltipByExceededMaxEnvelopes')
        : '',
    },
    {
      key: 'completed-card',
      value: completedEnvelopes,
      header: t('EnvelopesCompleted'),
      title: t('documents'),
      description: t('EnvelopesCompletedDescription'),
      color: 'success',
      icon: 'ni-check-circle',
      tooltip: exceededMaxEnvelopes
        ? t('EnvelopesCardTooltipByExceededMaxEnvelopes')
        : '',
    },
  ];

  return (
    <div className="nk-block mb-2">
      <div className="row g-gs">
        {summaryCards.map((card) => (
          <div className="col-12 col-md-4" key={card.key}>
            <div className={`card card-full rounded-xl bg-${card.color}`}>
              <div className="card-inner">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="fs-6 text-white text-opacity-75 mb-0">
                    {card.header}
                  </div>
                  <a href="#" className="link link-white text-light d-none">
                    Ver
                  </a>
                  <em className={`icon ni ${card.icon} text-white`} />
                </div>

                {isLoading ? (
                  <p className="placeholder-glow mb-0 pb-0 h-24px">
                    <span className="placeholder bg-white col-1 h-20px" />
                    <span className="placeholder bg-white col-6 ml-2 h-20px" />
                  </p>
                ) : (
                  <div className="d-flex">
                    <h5
                      className="fs-1 text-white text-nowrap"
                      data-tooltip-id={card.tooltip ? card.key : undefined}
                    >
                      {card.value} <small className="fs-3">{card.title}</small>
                    </h5>
                    {card.tooltip && (
                      <IconTooltip
                        id={card.key}
                        tooltip={card.tooltip}
                        className="text-white"
                      />
                    )}
                  </div>
                )}

                <div className="fs-11px text-white text-opacity-75 mt-1">
                  <span className="text-white" />
                  {card.description}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EnvelopesListSummarySection;
